import {
  BigPictureClient,
  Configuration,
  Event,
  Service,
  Versions,
} from '@vimeo/bigpicture-client';
import { deepSnakeCase } from '@vimeo/gctl-api/fetchers/_helpers';

import { defaultBpEventParams } from './constants';
import { BpEventParams, UserData } from './interface';
import { getVuid, getLanguage } from './utils';

const BP_EVENT_NAME = 'vimeo.upgrade_action';
const DEFAULT_BP_EVENT_VERSION = 41;

const BPActionType = {
  click: 'click',
  dismiss: 'dismiss',
  impression: 'impression',
};

const configure = (userData: UserData, userId?: number) => {
  const ciRegex: RegExp = /\.ci\.vimeows.com$/;

  const environment = ciRegex.test(window.location.host)
    ? 'staging'
    : window.location.host === 'vimeo.dev'
      ? 'development'
      : 'production';

  /* Init BigPictureConfig */
  const bpFrontendContext = new Event('vimeo.web_global', 5, {
    user: {
      subscription_type: userData.membership?.type,
      is_mod: userData.membership?.badge?.type === 'staff',
      session_id: null,
      teams: null,
      user_id: userId || null,
      vuid: getVuid(),
      flags: null,
      is_free_trial: userData.membership?.subscription?.trial?.status === 'free_trial',
    },
    application: {
      application: 'vimeo',
      vimeo_language: getLanguage() || 'en',
      product: 'upsell_modal',
      build_environment: environment,
      version: {
        full: '1.0.0',
        major: '1',
        minor: '0',
        patch: '0',
      },
    },
    platform: {
      device_language: window.navigator.language ? window.navigator.language : '',
      screen: {
        dpi: screen.pixelDepth,
        height: screen.height,
        width: screen.width,
        size: `${screen.width} x ${screen.height}`,
      },
      network: null,
      platform: 'web',
    },
  });

  const conf: Configuration = new Configuration(
    environment === 'production' ? Service.FRESNEL_PROD : Service.FRESNEL_DEV,
    bpFrontendContext,
  );

  BigPictureClient.configure(conf);
};

const emitBPEvent = (
  eventParams: BpEventParams,
  bpEventVersion: Versions<typeof BP_EVENT_NAME> = DEFAULT_BP_EVENT_VERSION,
) => {
  return BigPictureClient.sendEvent(
    new Event(
      BP_EVENT_NAME,
      bpEventVersion,
      deepSnakeCase({
        ...defaultBpEventParams,
        ...eventParams,
      }),
    ),
  );
};

const trackImpression = (
  eventParams: BpEventParams,
  bpEventVersion: Versions<typeof BP_EVENT_NAME> = DEFAULT_BP_EVENT_VERSION,
) => {
  emitBPEvent(
    {
      action_type: BPActionType.impression,
      ...eventParams,
    },
    bpEventVersion,
  );
};

const trackButtonClick = (
  eventParams: BpEventParams,
  bpEventVersion: Versions<typeof BP_EVENT_NAME> = DEFAULT_BP_EVENT_VERSION,
) => {
  emitBPEvent(
    {
      action_type: BPActionType.click,
      ...eventParams,
    },
    bpEventVersion,
  );
};

const trackDismiss = (
  eventParams: BpEventParams,
  bpEventVersion: Versions<typeof BP_EVENT_NAME> = DEFAULT_BP_EVENT_VERSION,
) => {
  emitBPEvent(
    {
      action_type: BPActionType.dismiss,
      ...eventParams,
    },
    bpEventVersion,
  );
};

const BpTracking = {
  configure,
  trackImpression,
  trackButtonClick,
  trackDismiss,
};

export default BpTracking;
